<template>
  <div
    class="conversation-detail-header-message"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- TEXT / ORIGINAL EMAIL TABS -->
    <div
      v-if="hasStylizedHtmlDescription && !messageEditing"
      class="conversation-detail-header-message__tabs"
    >
      <dd-button
        :class="{ 'conversation-detail-header-message__tab--active': !showingHtmlDescription }"
        size="small"
        type="text"
        class="conversation-detail-header-message__tab"
        @click="removeDefaultToHtmlDescription"
      >
        Description
      </dd-button>
      <dd-button
        :class="{ 'conversation-detail-header-message__tab--active': showingHtmlDescription }"
        size="small"
        type="text"
        class="conversation-detail-header-message__tab"
        @click="setDefaultToHtmlDescription"
      >
        Original Email
      </dd-button>
    </div>
    <!-- FIRST MESSAGE -->
    <div class="conversation-detail-header-message__view">
      <template v-if="messageEditing">
        <!-- EDIT FORM -->
        <editor
          v-model="internalMessage"
          :autofocus="true"
          name="message"
          placeholder="Edit incoming message..."
        />
        <div class="conversation-detail-header-message__view-actions">
          <dd-button type="text" color="grey" size="medium" @click="disableMessageEdit">Cancel</dd-button>
          <dd-button :color="ColorAssistant.MailboxColor" size="medium" @click="updateIncomingMessage" >Save Changes</dd-button>
        </div>
      </template>
      <template v-else>
        <!-- HTML -->
        <div v-if="showingHtmlDescription" class="html-description-wrapper">
          <iframe
            :src="creationEvent.stylizedHtmlVersionLink"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            frameborder="0"
            scrolling="auto" 
          />
        </div>
        <!-- PLAIN TEXT -->
        <div v-else class="description">
          <div
            class="user-inputed-rich-text"
            v-html="creationEvent.description"
          />
        </div>
      </template>

      <div v-if="hasOtherInfo" class="other-info">
        <!-- ATTACHMENTS -->
        <attachments-list :attachments="creationEvent.attachments" />
        <!-- Email delivery notes -->
        <email-delivery-notes
          :ccs="creationEvent.ccs"
          :auto-response-sent="creationEvent.autoResponseSent"
          :bounce-info="creationEvent.bounceInfo"
        />
      </div>

    </div>
    <!-- MORE OPTIONS -->
    <div
      v-if="!messageEditing && (showMoreOptions || $screen.isMobile($mq))"
      class="conversation-detail-header-message__more-options"
    >
      <template v-if="$screen.isTabletOrDesktop($mq)">
        <el-tooltip
          v-if="creationEvent.isOutsideUserInitiated && $permissions.isAdminOrOwner && !showingHtmlDescription"
          placement="top"
        >
          <i
            class="ddi ddi-edit"
            title="Edit this reply"
            @click="enableMessageEdit"
          />
          <template #content>
            <div>
              Edit this incoming message
            </div>
          </template>
        </el-tooltip>
        <el-tooltip placement="top">
          <i
            class="ddi ddi-task-add"
            @click="initiateLinkedTask(creationEvent.id)"
          />
          <template #content>
            <div>
              Create linked task from this message
            </div>
          </template>
        </el-tooltip>
      </template>
      <more-button-dropdown v-else-if="$screen.isMobile($mq)" :force-visibility="showMoreOptions">
        <template #options-list>
          <ul>
            <li
              v-if="creationEvent.isOutsideUserInitiated && $permissions.isAdminOrOwner && !showingHtmlDescription"
              @click="enableMessageEdit"
            >
              <i class="ddi ddi-edit" />
              <span>Edit this incoming message</span>
            </li>
            <li @click="initiateLinkedTask(creationEvent.id)">
              <i class="ddi ddi-task-add" />
              <span>Create linked task from this message</span>
            </li>
          </ul>
        </template>
      </more-button-dropdown>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import LocalStore from '@/helpers/localstore'
import TimeAssistant from '@/helpers/time-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IConvosOutputHistoryv12 } from '@/api/types/response.types'
import { useAppStore } from '@/modules/app/app.store'

/* API */

export default defineComponent({
  name: 'ConversationDetailHeaderMessage',

  props: {
    creationEvent: {
      type: Object as () => IConvosOutputHistoryv12,
      required: true,
    },
  },

  emits: ['update-incoming-message'],
  data() {
    return {
      TimeAssistant,
      ColorAssistant,
      internalMessage: '',
      messageEditing: false,
      defaultToHtmlDescription: false,
      showMoreOptions: false,
    }
  },
  computed: {
    hasStylizedHtmlDescription(): boolean {
      return (
        this.creationEvent.stylizedHtmlVersionLink !== null &&
        this.creationEvent.stylizedHtmlVersionLink.trim() !== ''
      )
    },
    showingHtmlDescription(): boolean {
      return this.hasStylizedHtmlDescription && this.defaultToHtmlDescription
    },
    avatarSize(): number {
      return this.$screen.isDesktop(this.$mq) ? 40 : 24
    },
    hasAttachments(): boolean {
      return this.creationEvent.attachments.length > 0
    },
    hasCCs(): boolean {
      return (
        this.creationEvent.ccs !== null && this.creationEvent.ccs.length > 0
      )
    },
    hasBounceInfo(): boolean {
      return this.creationEvent.bounceInfo !== null
    },
    hasOtherInfo(): boolean {
      return (
        this.hasAttachments ||
        this.hasCCs ||
        this.hasBounceInfo ||
        this.creationEvent.autoResponseSent
      )
    },
  },

  watch: {
    creationEvent: {
      immediate: true,
      handler(event: IConvosOutputHistoryv12) {
        this.internalMessage = event.description
      },
    },
  },

  mounted() {
    this.defaultToHtmlDescription =
      LocalStore.defaultToHtmlDescriptionOnConvoDetail
  },

  methods: {
    handleMouseEnter() {
      if (!this.$screen.isMobile(this.$mq)) {
        this.showMoreOptions = true
      }
    },
    handleMouseLeave() {
      if (!this.$screen.isMobile(this.$mq)) {
        this.showMoreOptions = false
      }
    },
    enableMessageEdit() {
      this.messageEditing = true
    },
    disableMessageEdit() {
      this.messageEditing = false
    },
    setDefaultToHtmlDescription() {
      LocalStore.defaultToHtmlDescriptionOnConvoDetail = true
      this.defaultToHtmlDescription = true
    },
    removeDefaultToHtmlDescription() {
      LocalStore.defaultToHtmlDescriptionOnConvoDetail = false
      this.defaultToHtmlDescription = false
    },
    initiateLinkedTask(id: number) {
      // This vuex prop is being watched by the task/convo detail header
      useAppStore().itemEventForLinkedTaskID = id
    },
    updateIncomingMessage() {
      this.messageEditing = false
      if (this.creationEvent.description !== this.internalMessage) {
        this.$emit('update-incoming-message', this.internalMessage)
      }
    },
  },
})
</script>

<style lang="scss">
.conversation-detail-header-message {
  position: relative;

  &__tabs {
    display: flex;
    padding: 20px 0 12px;
    border-bottom: $border--solid-light;
  }

  &__tab {
    position: relative;
    padding: 0;
    margin-right: 24px;
    @include lettering--p-sm;
    font-weight: $bold;

    &--active.dd-button--text {
      color: var(--color-text-secondary);

      &::after {
        position: absolute;
        right: 0;
        bottom: -13px;
        left: 0;
        height: 2px;
        content: '';
        background: $color--blue;
        border-radius: 1px;
      }
    }
  }

  &__view {
    padding-top: 16px;

    &-actions {
      display: flex;
      justify-content: flex-end;
      margin: 10px 0;
    }
  }

  .html-description-wrapper {
    iframe {
      width: 100%;
      border: $border--solid-extra-light;
      border-radius: 4px;
      min-height: 500px;
    }
  }

  .description {
    width: 100%;
    overflow: hidden;
  }

  .other-info {
    margin-top: $detail-description-bottom-buffer;
  }

  &__more-options {
    position: absolute;
    top: -24px;
    right: -8px;

    .more-button-dropdown {
      background-color: var(--color-background-header);
    }

    @include media-breakpoint-up(md) {
      display: flex;
      align-items: center;
      top: 0;
      right: 0;
      border: $border--solid-light;
      border-radius: 8px;
      padding: 4px 10px;
      background-color: var(--color-background-header);
      @include standard-box-shadow;

      .ddi {
        cursor: pointer;
        color: var(--color-text-third);
        padding: 4px 0;
      }

      .ddi-edit {
        padding-right: 10px;
        border-right: $border--solid-light;
        margin-right: 10px;
      }
    }
  }
}
</style>
